import { createResponderEventSource } from 'features/Responder/utils/createResponderEventSource';
import Cookies from 'js-cookie';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from 'services/_infra/apiClient';

const PersonnelContext = createContext();

export const PersonnelProvider = ({ children }) => {
  const [personnel, setPersonnel] = useState({});
  const [geolocationHistory, setGeolocationHistory] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [allEvents, setAllEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await apiClient.get(`/responder/event-history`);
      if (res?.data?.results) {
        setAllEvents(res.data.results);
      }
    } catch (err) {
      console.error('Failed to fetch events', err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  const updatePersonnelInMemory = useCallback((personnelUpdate) => {
    const normalizedUpdates = (() => {
      if (!personnelUpdate) return [];
      if (Array.isArray(personnelUpdate)) return personnelUpdate;
      if (personnelUpdate?.personnel) {
        return Array.isArray(personnelUpdate.personnel)
          ? personnelUpdate.personnel
          : Object.values(personnelUpdate.personnel);
      }
      if (personnelUpdate?.userId != null) return [personnelUpdate];
      if (typeof personnelUpdate === 'object') return Object.values(personnelUpdate);
      return [];
    })();

    if (normalizedUpdates.length === 0) return;

    setPersonnel((prevPersonnel) => {
      const updated = { ...prevPersonnel };
      normalizedUpdates.forEach((person) => {
        if (person?.userId == null) return;
        // Merge the update into the existing person object, if present
        updated[person.userId] = {
          ...(updated[person.userId] || {}),
          ...person,
        };
      });
      return updated;
    });
  }, []);

  const updateGeolocationHistoryInMemory = useCallback((geolocationUpdate) => {
    if (!geolocationUpdate?.userId) return;
    setGeolocationHistory((prevHistory) => {
      const updated = { ...prevHistory };
      const nextEntries = updated[geolocationUpdate.userId]
        ? [...updated[geolocationUpdate.userId], geolocationUpdate]
        : [geolocationUpdate];
      updated[geolocationUpdate.userId] = nextEntries;
      return updated;
    });
  }, []);

  const normalizeEventEntries = useCallback((payload, fallbackEvent) => {
    const items = Array.isArray(payload) ? payload : [payload];
    return items
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        return {
          ...item,
          event: item.event || item.type || fallbackEvent,
        };
      })
      .filter(Boolean);
  }, []);

  const fetchPersonnel = useCallback(async () => {
    try {
      const res = await apiClient.get(`/responder/personnel`);
      if (res?.data?.personnel) {
        // Convert personnel array/object to an object keyed by userId
        const personnelArray = Array.isArray(res.data.personnel)
          ? res.data.personnel
          : Object.values(res.data.personnel);
        const personnelById = {};
        personnelArray.forEach((person) => {
          if (person?.userId != null) {
            personnelById[person.userId] = person;
          }
        });
        setPersonnel(personnelById);
        setLastUpdateTime(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch personnel', err);
    }
  }, []);

  const token = Cookies.get('accessToken');

  useEffect(() => {
    if (!token) {
      fetchPersonnel();
      return undefined;
    }

    const sse = createResponderEventSource({
      path: '/responder/notifier_Event',
      eventListeners: {
        'personnel-update': (event) => {
          try {
            const data = JSON.parse(event.data);
            // console.log('Received personnel-update SSE', data);
            updatePersonnelInMemory(data);
            setLastUpdateTime(new Date());
          } catch (err) {
            console.error('Failed to parse personnel SSE payload', err);
          }
        },
        'geolocation-trigger': (event) => {
          try {
            const data = JSON.parse(event.data);
            // console.log('Received geolocation-trigger SSE', data);
            updateGeolocationHistoryInMemory(data.data);
            setLastUpdateTime(new Date());
            const normalized = normalizeEventEntries(data.data, 'geolocation-trigger');
            if (normalized.length) {
              setAllEvents((prev) => [...prev, ...normalized]);
            }
          } catch (err) {
            console.error('Failed to parse geolocation SSE payload', err);
          }
        },
      },
      onOpen: () => {
        console.log('Personnel SSE connection opened');
      },
      onError: (err) => {
        console.error('Personnel SSE error', err);
      },
    });

    fetchPersonnel();

    return () => {
      sse.close();
    };
  }, [
    fetchPersonnel,
    token,
    normalizeEventEntries,
    updateGeolocationHistoryInMemory,
    updatePersonnelInMemory,
  ]);

  const contextValue = {
    personnel,
    setPersonnel,
    geolocationHistory,
    setGeolocationHistory,
    fetchPersonnel,
    lastUpdateTime,
    allEvents,
  };

  return <PersonnelContext.Provider value={contextValue}>{children}</PersonnelContext.Provider>;
};

export const usePersonnel = () => {
  const context = useContext(PersonnelContext);
  if (!context) {
    throw new Error('usePersonnel must be used within a PersonnelProvider');
  }
  return context;
};
